import { dedupeOrderedAssetTokens, formatAssetTokenLabel } from './assetTokens';

const PROVIDER_METADATA = Object.freeze({
  botspot_auto: {
    name: 'BotSpot Auto',
    recommended: true,
    capabilities: ['stocks', 'options', 'indexes', 'crypto', 'futures'],
    requiredEnv: [],
    envDefaults: {},
    backtestingDataSource: '{"default":"ibkr","stock":"ibkr","index":"ibkr","option":"thetadata","crypto":"coinbase","crypto_future":"coinbase","future":"ibkr","cont_future":"ibkr"}',
  },
  theta_data: {
    name: 'Theta Data',
    recommended: false,
    capabilities: ['stocks', 'options', 'indexes'],
    requiredEnv: [],
    envDefaults: {},
    backtestingDataSource: 'ThetaData',
  },
  polygon: {
    name: 'Polygon',
    capabilities: ['stocks', 'options', 'indexes', 'crypto'],
    requiredEnv: ['POLYGON_API_KEY'],
    envDefaults: {},
    backtestingDataSource: 'polygon',
  },
  alpaca: {
    name: 'Alpaca',
    capabilities: ['stocks', 'options', 'crypto'],
    requiredEnv: ['ALPACA_API_KEY', 'ALPACA_API_SECRET'],
    envDefaults: { ALPACA_IS_PAPER: 'true' },
    backtestingDataSource: 'alpaca',
  },
  databento: {
    name: 'DataBento',
    capabilities: ['futures'],
    requiredEnv: ['DATABENTO_API_KEY'],
    envDefaults: {},
    backtestingDataSource: 'DataBento',
  },
});

const PREFERRED_ORDER = ['botspot_auto', 'theta_data'];

const buildCapabilityLabel = (tokens = []) => {
  if (!Array.isArray(tokens) || tokens.length === 0) return '';
  return tokens.map((token) => formatAssetTokenLabel(token)).join(' · ');
};

export const getProviderMetadata = (slug) => {
  if (!slug) return null;
  const key = slug.trim().toLowerCase();
  return PROVIDER_METADATA[key] || null;
};

export const decorateProvider = (provider) => {
  if (!provider) return provider;
  const meta = getProviderMetadata(provider.slug) || {};
  const baseName = meta.name || provider.name || provider.slug;
  const supportedFromApi =
    Array.isArray(provider?.capabilities?.supported) && provider.capabilities.supported.length > 0
      ? provider.capabilities.supported
      : [];

  const supported = dedupeOrderedAssetTokens(
    supportedFromApi.length > 0 ? supportedFromApi : meta.capabilities || [],
  );

  const capabilityLabel =
    provider?.capabilities?.label ||
    buildCapabilityLabel(supported.length > 0 ? supported : meta.capabilities || []);

  return {
    ...provider,
    name: baseName,
    displayName: meta.recommended ? `${baseName} (recommended)` : baseName,
    recommended: Boolean(meta.recommended),
    capabilities: {
      supported,
      label: capabilityLabel,
    },
    requiredEnv: Array.isArray(meta.requiredEnv) ? meta.requiredEnv : [],
    envDefaults: typeof meta.envDefaults === 'object' && meta.envDefaults !== null ? meta.envDefaults : {},
  };
};

export const decorateProviders = (providers = []) => {
  if (!Array.isArray(providers)) return [];
  return providers.map((provider) => decorateProvider(provider));
};

export const sortProviders = (providers = []) => {
  const orderIndex = new Map(PREFERRED_ORDER.map((slug, idx) => [slug, idx]));
  return [...providers].sort((a, b) => {
    // Custom provider always goes LAST - it's a last resort fallback
    if (a.slug === 'custom' && b.slug !== 'custom') return 1;
    if (b.slug === 'custom' && a.slug !== 'custom') return -1;

    if (a.recommended && !b.recommended) return -1;
    if (b.recommended && !a.recommended) return 1;

    const aOrder = orderIndex.has(a.slug) ? orderIndex.get(a.slug) : Number.POSITIVE_INFINITY;
    const bOrder = orderIndex.has(b.slug) ? orderIndex.get(b.slug) : Number.POSITIVE_INFINITY;
    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }

    const aName = a.displayName || a.name || a.slug;
    const bName = b.displayName || b.name || b.slug;
    return String(aName).localeCompare(String(bName));
  });
};

export const buildProviderOptions = (providers = []) => sortProviders(decorateProviders(providers));

export const getProviderRequiredEnvVars = (slug) => {
  return getProviderMetadata(slug)?.requiredEnv || [];
};

export const getProviderEnvDefaults = (slug) => {
  return getProviderMetadata(slug)?.envDefaults || {};
};

export const getProviderCapabilitiesFromMeta = (slug) => {
  return getProviderMetadata(slug)?.capabilities || [];
};

export const getProviderBacktestingDataSource = (slug) => {
  return getProviderMetadata(slug)?.backtestingDataSource || null;
};

export const getAllProviderSpecificEnvKeys = () => {
  const keys = new Set();
  Object.values(PROVIDER_METADATA).forEach((meta) => {
    (meta.requiredEnv || []).forEach((key) => keys.add(key));
  });
  return Array.from(keys);
};
